import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import classNames from 'classnames';

import Tooltip from '@material-ui/core/Tooltip';

import { iconColors as colors } from '../../colors';
import { toggleVideoMute, togglePublishVideo } from '../../lib/actions/room';
import { logEvent, Event } from '../../lib/analytics';
import { UserActionsContext } from '../../lib/contexts';
import useStyles from '../../style/ControlButton';
import {
  IconVideo,
  IconVideoOff,
} from '../IconSet';
import { mapStateToProps, MappedProps } from '../Meeting/VideoToolbar/mapState';
import ResponsiveButton from '../ResponsiveButton';


const messages = defineMessages({
  videoButtonEnableTooltip: { id: 'videoButtonEnableTooltip' },
  videoButtonDisableTooltip: { id: 'videoButtonDisableTooltip' },
});


function VideoMuteButton(props: ExtendedProps) {
  const classes = useStyles();
  const userActionsContext = React.useContext(UserActionsContext);

  const {
    mySelf,
    hasVideoStream,
    canPublishStream,
    isVideoMuted,
    uid,
    iconSize = 20,
    iconColor = colors.normal,
    mini,
  } = props;

  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const toggleMute = React.useCallback(
    () => {
      const action = !isVideoMuted ? 'mute' : 'unmute';
      if (mySelf) {
        dispatch(togglePublishVideo(!isVideoMuted));
        logEvent(Event.MUTE_MY_VIDEO, { 'action': action, 'from': userActionsContext });
      }
      else {
        dispatch(toggleVideoMute(uid, !isVideoMuted));
        logEvent(Event.MUTE_USER_VIDEO, { 'action': action, 'from': userActionsContext });
      }
    }
    , [dispatch, isVideoMuted, uid, mySelf, userActionsContext]
  );

  const controlButtonHidden = (!hasVideoStream || !canPublishStream) ? classes.controlButtonHidden : null;
  const msg = formatMessage(
    (isVideoMuted) ? messages.videoButtonEnableTooltip : messages.videoButtonDisableTooltip
  );

  const cls = classNames(
    classes.controlButton,
    props.cName,
    controlButtonHidden);

  // add div due to https://github.com/mui-org/material-ui/issues/8416#issuecomment-332556082
  return (
    <Tooltip placement="top" title={(!hasVideoStream || !canPublishStream || mini) ? '' : msg}>
      <div>
        <ResponsiveButton
          disabled={!hasVideoStream || !canPublishStream}
          cName={cls}
          mini={mini}
          onClick={toggleMute}
          size='small'
          text={msg}>
          {isVideoMuted
            ? <IconVideoOff color={colors.inactive} size={iconSize} />
            : <IconVideo color={iconColor} size={iconSize} />
          }
        </ResponsiveButton>
      </div>
    </Tooltip>
  );
}


type Props = {
  uid: string;
  iconSize?: number;
  iconColor?: string;
  cName?: string;
  mini?: boolean;
}


type ExtendedProps = Props & MappedProps


export default connect(mapStateToProps)(VideoMuteButton);
